import * as React from "react";
import { toast } from "@/hooks/use-toast";
import { getFunctions, httpsCallable } from 'firebase/functions';

export function ContactSection() {
  const [form, setForm] = React.useState({ name: "", email: "", message: "" });
  const [loading, setLoading] = React.useState(false);

  const onChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setForm((f) => ({ ...f, [e.target.name]: e.target.value }));
  };

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Form validation
    if (!form.name || !form.email || !form.message) {
      toast({
        title: "Please fill all fields.",
        variant: "destructive",
      });
      return;
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(form.email)) {
      toast({
        title: "Invalid email address",
        description: "Please enter a valid email address.",
        variant: "destructive",
      });
      return;
    }

    setLoading(true);

    try {
      // Get Firebase Functions instance
      const functions = getFunctions();
      const sendEmail = httpsCallable(functions, 'sendContactFormEmail');
      
      // Call the Firebase Function
      const result = await sendEmail({
        name: form.name,
        email: form.email,
        message: form.message
      });

      console.log('Email sent successfully:', result.data);

      // Show success message
      toast({
        title: "Thank you!",
        description: "Your message has been sent. We'll get back to you soon!",
      });

      // Reset form
      setForm({ name: "", email: "", message: "" });
    } catch (error: any) {
      console.error('Error sending message:', error);
      
      let errorMessage = "Failed to send message. Please try again later.";
      
      if (error.code === 'functions/invalid-argument') {
        errorMessage = error.message;
      } else if (error.code === 'functions/internal') {
        errorMessage = "Server error. Please try again later.";
      }
      
      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" className="py-24 bg-gradient-to-b from-[#F9F7F1] to-white">
      <div className="max-w-2xl mx-auto px-5">
        <h2 
          className="text-4xl font-playfair font-bold text-gray-800 mb-12 text-center"
          data-aos="fade-down"
        >
          Contact Us
        </h2>
        <div 
          className="bg-white rounded-2xl shadow-lg p-8"
          data-aos="fade-up"
          data-aos-delay="200"
        >
          <form 
            onSubmit={onSubmit} 
            className="space-y-6"
          >
          <input
            type="text"
              id="name"
            name="name"
            value={form.name}
            onChange={onChange}
            placeholder="Your Name"
              className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#FFA500] focus:border-transparent outline-none transition-all bg-white placeholder-gray-400"
              required
          />
          <input
            type="email"
              id="email"
            name="email"
            value={form.email}
            onChange={onChange}
            placeholder="Your Email"
              className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#FFA500] focus:border-transparent outline-none transition-all bg-white placeholder-gray-400"
              required
          />
          <textarea
              id="message"
            name="message"
            value={form.message}
            onChange={onChange}
            placeholder="Your Message"
            rows={5}
              className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#FFA500] focus:border-transparent outline-none transition-all bg-white placeholder-gray-400 resize-none"
              required
          />
          <button
            type="submit"
            disabled={loading}
              className="w-full bg-[#D7263D] text-white py-4 rounded-lg font-semibold hover:bg-[#FF6B35] transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? "Sending..." : "Send Message"}
          </button>
        </form>
        </div>
      </div>
    </section>
  );
}
